import './filesDistributionByTime.less'
import {
  ACTION_FETCH_PCI_LAST_MODIFIED_DATA,
  ACTION_FETCH_PCI_LAST_ACCESSED_DATA,
  fetchPCILastModifiedData,
  fetchPCILastAccessedData
} from '../../../complianceDashboardSlice'
import ChartBarSimple, {
  ChartBarDataItem
} from '../../../../../../components/ChartBarSimple/chartBarSimple'
import CustomTooltip from '../../../../../../components/ChartTooltipV2'
import LoaderView from '../../../../../../components/Loader/loader'
import useWindowSize from '../../../../../../hooks/useWindowResize'
import { getShowLoader } from '../../../../../../reducers/requestReducer'
import { RootState } from '../../../../../../rootReducer'
import { getWindowProps } from '../../../../../../utils'
import {
  UNIQUE_WIDGET_INDEX,
  getUniqueWidgetIndex,
  ChartColors
} from '../../../../mainDashboardv2/constants'
import { mockFilesDistributionByTime } from '../../mocks'
import WidgetTitle from '../../../../../../components/WidgetTitle'
import Card from '../../../../../accessControl/components/card'
import FooterAlertIcon from '../../../../../../assets/inlineSvg/alert-oct-icon.svg'
import EmptyStateIcon from '../../../../../../assets/inlineSvg/widgets-empty-state.svg'
import { hasData } from '../../helper'
import { EmptyState } from '../../emptyState/emptyState'
import { useDashboardType } from '../../hooks/useDashboardType'
import { DashboardCategory } from '../../../../mainDashboardv2/slice'
import { Typography, Icon, Tooltip } from '@lightbeamai/design-system'
import React, { useEffect, useState, useMemo } from 'react'
import { FormattedNumber, useIntl } from 'react-intl'
import { connect } from 'react-redux'

export const FILES_DISTRIBUTION_TABS = {
  LAST_MODIFIED: 'last_modified',
  LAST_ACCESSED: 'last_accessed'
} as const

export type FilesDistributionTabType = typeof FILES_DISTRIBUTION_TABS[keyof typeof FILES_DISTRIBUTION_TABS]

interface OwnProps {
  useMockData?: boolean
  chartBarAlignment?: 'vertical' | 'horizontal'
  widgetDatasourceId?: string
  widgetDashboardType?: string
  widgetDashboardId?: string
}

interface StateProps {
  lastModifiedData?: any
  lastAccessedData?: any
  showLastModifiedLoader: boolean
  showLastAccessedLoader: boolean
}

interface DispatchProps {
  fetchPCILastModifiedData: (params: { [UNIQUE_WIDGET_INDEX]?: string }) => void
  fetchPCILastAccessedData: (params: { [UNIQUE_WIDGET_INDEX]?: string }) => void
}

type FilesDistributionByTimeComponentProps = StateProps & DispatchProps & OwnProps

const FilesDistributionByTime = (props: FilesDistributionByTimeComponentProps) => {
  const {
    lastModifiedData,
    lastAccessedData,
    showLastModifiedLoader,
    showLastAccessedLoader,
    chartBarAlignment = 'horizontal',
    useMockData = false,
    widgetDatasourceId,
    widgetDashboardId,
    widgetDashboardType,
    fetchPCILastModifiedData,
    fetchPCILastAccessedData
  } = props

  const intl = useIntl()

  const { isPciDashboard } = useDashboardType(widgetDashboardType as DashboardCategory)

  const windowProps = getWindowProps(useWindowSize())
  const { isLargeScreen } = windowProps

  const titleTabs = [
    {
      key: FILES_DISTRIBUTION_TABS.LAST_MODIFIED,
      name: intl.formatMessage({
        id: 'compliance.dashboard.pci.widget.filesDistributionByTime.tab.lastModified'
      })
    },
    {
      key: FILES_DISTRIBUTION_TABS.LAST_ACCESSED,
      name: intl.formatMessage({
        id: 'compliance.dashboard.pci.widget.filesDistributionByTime.tab.lastAccessed'
      })
    }
  ]

  const [activeTab, setActiveTab] = useState<FilesDistributionTabType>(titleTabs[0].key)

  const handleTabChange = (tab) => setActiveTab(tab)

  useEffect(() => {
    if (useMockData) return

    const widgetIndex = getUniqueWidgetIndex(widgetDashboardId, widgetDatasourceId)
    const params = { [UNIQUE_WIDGET_INDEX]: widgetIndex }

    fetchPCILastModifiedData(params)
    fetchPCILastAccessedData(params)
  }, [
    useMockData,
    fetchPCILastModifiedData,
    fetchPCILastAccessedData,
    widgetDashboardId,
    widgetDatasourceId
  ])

  const currentWidgetData = useMemo(() => {
    if (activeTab === FILES_DISTRIBUTION_TABS.LAST_MODIFIED) {
      return lastModifiedData
    } else {
      return lastAccessedData
    }
  }, [activeTab, lastModifiedData, lastAccessedData])

  const showLoader = useMemo(() => {
    if (activeTab === FILES_DISTRIBUTION_TABS.LAST_MODIFIED) {
      return showLastModifiedLoader
    } else {
      return showLastAccessedLoader
    }
  }, [activeTab, showLastModifiedLoader, showLastAccessedLoader])

  const totalBackendCount = useMemo(() => {
    if (!currentWidgetData || !Array.isArray(currentWidgetData)) return 0
    return currentWidgetData.reduce((sum, item) => sum + (item.count || 0), 0)
  }, [currentWidgetData])

  const getTimeRangeLabel = (timeRange: string, intl: any) => {
    const translations = {
      LAST_3_MONTHS: intl.formatMessage({
        id: 'compliance.dashboard.widgets.filesDistributionByTime.ranges.last3Months'
      }),
      LAST_6_MONTHS: intl.formatMessage({
        id: 'compliance.dashboard.widgets.filesDistributionByTime.ranges.last6Months'
      }),
      LAST_1_YEAR: intl.formatMessage({
        id: 'compliance.dashboard.widgets.filesDistributionByTime.ranges.last1Year'
      }),
      LAST_3_YEARS: intl.formatMessage({
        id: 'compliance.dashboard.widgets.filesDistributionByTime.ranges.last3Years'
      }),
      MORE_THAN_3_YEARS: intl.formatMessage({
        id: 'compliance.dashboard.widgets.filesDistributionByTime.ranges.moreThan3Years'
      })
    }
    return translations[timeRange] || timeRange
  }

  const chartData: ChartBarDataItem[] = useMemo(() => {
    if (!currentWidgetData || !Array.isArray(currentWidgetData)) return []

    return currentWidgetData.map((item) => ({
      name: getTimeRangeLabel(item.timeRange, intl),
      value: item.count || 0,
      percentage: totalBackendCount > 0 ? +((item.count / totalBackendCount) * 100).toFixed(2) : 0,
      timeRange: item.timeRange,
      dataKey: item.timeRange
    }))
  }, [currentWidgetData, totalBackendCount, intl])

  const yLabelFormatter = (value: number) => intl.formatNumber(value, { notation: 'compact' })

  const footerData: ChartBarDataItem = useMemo(() => {
    return (
      chartData.find((item: ChartBarDataItem) => item.timeRange === 'MORE_THAN_3_YEARS') || {
        name: '',
        value: 0,
        percentage: 0,
        timeRange: 'MORE_THAN_3_YEARS',
        dataKey: 'MORE_THAN_3_YEARS'
      }
    )
  }, [chartData])

  const cardTitle = (
    <WidgetTitle
      title={
        <div className="widget-title">
          <Typography color="primary" variant="h6" as="span">
            {intl.formatMessage({
              id: 'compliance.dashboard.pci.widget.filesDistributionByTime.title'
            })}
          </Typography>
          <Tooltip
            title={intl.formatMessage({
              id: isPciDashboard
                ? 'compliance.dashboard.pci.widget.title.tooltip'
                : 'compliance.dashboard.hipaa.widget.title.tooltip'
            })}
            placement="right"
          >
            <Icon size={16} name="InfoCircle" className="pointer" />
          </Tooltip>
        </div>
      }
      tabs={titleTabs}
      onChange={handleTabChange}
      activeTab={activeTab}
      bordered
    />
  )

  const hasChartData = hasData(chartData)

  return (
    <Card className="files-distribution-by-time-widget" cardTitle={cardTitle}>
      <div className={`files-distribution-by-time-chart-wrapper ${chartBarAlignment}`}>
        {hasChartData ? (
          <div className="charts-wrapper files-distribution-by-time-bar-charts">
            <ChartBarSimple
              className="xs-mt-8 v2-chart"
              data={chartData}
              cartesianGridHorrizontal
              tickFormatter={{
                fontSize: '0.72rem',
                fill: ChartColors.SecondaryText,
                width: 80,
                textAnchor: 'middle'
              }}
              barColor={ChartColors.Blue}
              chartMargin={{ left: 20, right: 20, top: 0, bottom: 0 }}
              barSize={60}
              chartResponsiveHeight={isLargeScreen ? 240 : 200}
              {...(isLargeScreen ? { xAxisHeight: 55 } : {})}
              yLabel={intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.filesDistributionByTime.chart.yLabel'
              })}
              xAxisAngle={0}
              YTickFormatter={yLabelFormatter}
              yAxisLabelPadding={{ dx: -12, dy: -6 }}
              customToolTip={<CustomTooltip />}
              tickMargin={4}
              xAxisTickInterval={0}
            />
          </div>
        ) : (
          <EmptyState
            title={intl.formatMessage({
              id: 'docAnalytics.widget.noFilesFound'
            })}
            icon={<EmptyStateIcon />}
          />
        )}
      </div>

      {hasChartData && (
        <div className="files-distribution-footer-wrapper">
          <div className="footer-label">
            <FooterAlertIcon />

            <Typography color="primary" variant="baseMinus2" type="strong" as="span">
              {intl.formatMessage({
                id: 'compliance.dashboard.pci.widget.filesDistributionByTime.footer.text'
              })}
            </Typography>
          </div>
          <div className="footer-values">
            <Typography as="span" color="primary" type="strong" variant="base">
              <FormattedNumber value={footerData.value as number} notation="compact" />
            </Typography>
            <Typography as="span" color="primary" type="strong" variant="base">
              <FormattedNumber
                value={footerData.percentage as number}
                notation="compact"
                maximumFractionDigits={2}
              />
              %
            </Typography>
          </div>
        </div>
      )}

      <LoaderView size="small" showLoader={showLoader} />
    </Card>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const widgetIndex = getUniqueWidgetIndex(ownProps.widgetDashboardId, ownProps.widgetDatasourceId)

  return {
    lastModifiedData: ownProps?.useMockData
      ? mockFilesDistributionByTime.data
      : state.complianceDashboard.pciLastModifiedData?.[widgetIndex],
    lastAccessedData: ownProps?.useMockData
      ? mockFilesDistributionByTime.data
      : state.complianceDashboard.pciLastAccessedData?.[widgetIndex],
    showLastModifiedLoader: getShowLoader(ACTION_FETCH_PCI_LAST_MODIFIED_DATA),
    showLastAccessedLoader: getShowLoader(ACTION_FETCH_PCI_LAST_ACCESSED_DATA)
  }
}

const mapDispatchToProps: DispatchProps = {
  fetchPCILastModifiedData,
  fetchPCILastAccessedData
}

export default connect(mapStateToProps, mapDispatchToProps)(FilesDistributionByTime)
